import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { ReactNode, memo, useCallback } from 'react'
import { openSettingsDrawer } from '../../../../app-state/modals/state.drawer'
import { isLounge } from '../../../../mfcLogic/userProperties/user'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import { AppStateType, useAppStateSelector } from '../../../Hooks/useAppState'
import { useRouteSelector } from '../../../Hooks/useRoute'
import { getThemeSpacing } from '../../../styles/appTheme'
import shape from '../../../styles/shape'
import Grid from '../../Grid'
import { useHomeData } from '../../Home/useHomeData'
import { getRows } from './helpers'

const StyledGridContainer = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    borderTopLeftRadius: shape.borderRadius,
    borderTopRightRadius: shape.borderRadius,
})

const darkModeRoundedCorners = css({
    borderRadius: shape.borderRadius,
})

const StyledRoot = styled('div')({
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    display: 'flex',
})
const StyledContainer = styled('div')({
    maxWidth: 600,
    padding: getThemeSpacing(1),
    flexDirection: 'column',
    margin: 'auto',
})
const StyledMessageContainer = styled('div')({
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: 600,
    textAlign: 'center',
})

// const portraitStyles = css({
//     flexFlow: 'column nowrap',
//     willChange: 'transform',
// })
const landscapeStyles = css({
    flexFlow: 'row nowrap',
    willChange: 'transform',
})

const EmptyListWarning = memo(() => {
    const onOpenSetttings = useCallback(() => {
        openSettingsDrawer()
    }, [])
    return (
        <StyledRoot data-testid="Home__EmptyListWarning">
            <StyledContainer>
                <StyledMessageContainer>
                    {i18n.t('home.noModels')}
                </StyledMessageContainer>
                <br />
                <Button size="large" onClick={onOpenSetttings}>
                    {i18n.t('home.openSettings')}
                </Button>
            </StyledContainer>
        </StyledRoot>
    )
})

interface GridContainerProps {
    noModelsVisible: boolean
    children?: ReactNode
    // sidePins: boolean
}

const GridContainer = memo((props: GridContainerProps) => {
    const { noModelsVisible, children } = props

    const darkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const gridContainerStyles = [landscapeStyles] //[!sidePins ? portraitStyles : landscapeStyles]
    if (darkMode) gridContainerStyles.push(darkModeRoundedCorners)

    return (
        <StyledGridContainer css={gridContainerStyles}>
            {!noModelsVisible ? <>{children}</> : <EmptyListWarning />}
        </StyledGridContainer>
    )
})

interface IAvailableSpaceGrid {
    activeRoom: number
    width: number
    height: number
}

const AvailableSpaceModelGrid = memo((props: IAvailableSpaceGrid) => {
    const { activeRoom, width, height } = props
    const { noModelsVisible, enableOnScroll, onScroll, isMobile, ...other } =
        useHomeData()

    const routeSlug = useRouteSelector('slug')
    const inLounge = isLounge(routeSlug)
    const loungeSlug = inLounge ? routeSlug : undefined

    return (
        <GridContainer noModelsVisible={noModelsVisible}>
            <Grid
                overrideHeight={height}
                data={{
                    ...other,
                    roomWidth: width,
                    activeRoom,
                    loungeSlug,
                }}
                enableOnScroll={enableOnScroll}
                onScroll={onScroll}
                getRows={getRows}
            />
        </GridContainer>
    )
})

export default AvailableSpaceModelGrid
